import { FormEvent, useState } from "react";
import { motion } from "framer-motion";
import { SEO } from "../components/SEO";
import { SuccessPopup } from "../components/SuccessPopup";

export function ContactPage() {
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);

  return (
    <>
      <SEO
        title="Contact Valkyrie Robotics"
        description="Get in touch with Valkyrie Robotics for VEX Robotics coaching in Forsyth County, GA. Schedule a free trial session or ask questions about our programs. Call (470) 239-0787 or send us a message."
        canonical="/contact"
        keywords={[
          "contact Valkyrie Robotics",
          "VEX Robotics coaching contact",
          "robotics coaching inquiry",
          "free trial session",
          "Forsyth County robotics",
          "robotics program questions",
          "VEX IQ contact",
          "VEX V5 contact",
        ]}
      />
      <div className="bg-neutral-900">
        <section className="mx-auto max-w-4xl px-4 py-16 text-gray-100">
          <div className="text-center mb-12">
            <h1 className="text-3xl md:text-4xl font-bold mb-4">
              Get in Touch
            </h1>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Ready to start your child's robotics journey? Have questions about
              our programs? We'd love to hear from you and help get your student
              started.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Contact Form */}
            <div className="bg-neutral-800/50 rounded-xl p-8 border border-neutral-700">
              <h2 className="text-2xl font-semibold mb-6">Send us a Message</h2>
              <form
                name="contact"
                method="POST"
                action="/"
                data-netlify="true"
                data-netlify-honeypot="bot-field"
                data-netlify-recaptcha="false"
                className="space-y-6"
                                onSubmit={async (e: FormEvent) => {
                  e.preventDefault();
 
                  // Show success popup immediately for better UX
                  setShowSuccessPopup(true);
 
                  // Submit form data to Netlify without redirecting
                  const form = e.target as HTMLFormElement;
                  const formData = new FormData(form);
                  
                  try {
                    // Submit to Netlify Forms endpoint
                    await fetch('/', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                      body: new URLSearchParams(formData as any).toString(),
                    });
                    
                    // Form submitted successfully - popup is already showing
                    // Reset form for next use
                    form.reset();
                  } catch (error) {
                    console.error('Error submitting form:', error);
                    // Even if submission fails, user still sees the popup
                  }
                }}
              >
                {/* Hidden input for Netlify form processing */}
                <input type="hidden" name="form-name" value="contact" />
                <p className="hidden">
                  <label>
                    Don't fill this out if you're human:{" "}
                    <input name="bot-field" />
                  </label>
                </p>

                <div>
                  <label className="block text-sm font-medium text-gray-200 mb-2">
                    Name *
                  </label>
                  <input
                    required
                    name="name"
                    placeholder="Your full name"
                    className="w-full rounded-lg border border-neutral-600 bg-neutral-700 px-4 py-3 text-gray-100 placeholder-gray-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 focus:outline-none transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-200 mb-2">
                    Email *
                  </label>
                  <input
                    required
                    type="email"
                    name="email"
                    placeholder="your.email@example.com"
                    className="w-full rounded-lg border border-neutral-600 bg-neutral-700 px-4 py-3 text-gray-100 placeholder-gray-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 focus:outline-none transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-200 mb-2">
                    Message *
                  </label>
                  <textarea
                    required
                    name="message"
                    rows={5}
                    placeholder="Tell us about your child's robotics experience, questions about our programs, or anything else you'd like to know..."
                    className="w-full rounded-lg border border-neutral-600 bg-neutral-700 px-4 py-3 text-gray-100 placeholder-gray-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 focus:outline-none transition-colors resize-none"
                  />
                </div>

                <button
                  className="w-full rounded-lg bg-brand-600 text-neutral-900 font-semibold px-6 py-3 hover:bg-brand-500 active:scale-95 transition-all duration-150 ease-out"
                  type="submit"
                >
                  Send Message
                </button>
              </form>
            </div>

            {/* Contact Information */}
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-semibold mb-6">
                  Other Ways to Reach Us
                </h2>
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-brand-600/20 rounded-lg flex items-center justify-center">
                      <svg
                        className="w-6 h-6 text-brand-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M3 5a2 2 0 012-2h3.28a1 1 0 01.554.894l1.12 4.4a2 2 0 00.6 1.1l3.5 3.5a2 2 0 010 2.83l-3.5 3.5a2 2 0 01-1.1.6l-4.4 1.12a1 1 0 01-.894.554H5a2 2 0 01-2-2V5z"
                        />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-100">Phone</h3>
                      <a
                        href="tel:+14702390787"
                        className="text-brand-500 hover:text-brand-400 transition-colors text-lg font-medium"
                      >
                        (470) 239-0787
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-brand-600/20 rounded-lg flex items-center justify-center">
                      <svg
                        className="w-6 h-6 text-brand-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-100">Email</h3>
                      <a
                        href="mailto:contact@valkyrie-robotics.com"
                        className="text-brand-500 hover:text-brand-400 transition-colors text-lg font-medium"
                      >
                        contact@valkyrie-robotics.com
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Success Popup */}
      <SuccessPopup
        isOpen={showSuccessPopup}
        onClose={() => setShowSuccessPopup(false)}
        title="Message Sent Successfully!"
        message="Thank you for contacting Valkyrie Robotics. We've received your message and will get back to you within 24 hours about our VEX Robotics coaching programs."
      />
    </>
  );
}
